﻿import React, { useState, useEffect, useRef } from "react";
import URLSearchParams from "@ungap/url-search-params";
import { setSearchHistory } from "../../../../../Resources/Scripts/_Shared/history";
import { FilterFacilities } from "./Filters";
import { search } from "../Endpoints";
import { DateSelectionWithLabel } from "../../../../../Resources/Scripts/_Shared/elements/DateSelectionWithLabel";
import moment from "moment";
import { parseDate } from "../../../../../Resources/Scripts/_Shared/parseDate";

const TextboxWidget = ({
    isLoaded,
    facilitySearchContext,
    setFacilitySearchContext,
}) => {
    const lang = facilitySearchContext.localization.inputs;
    const textboxRef = useRef(null);
    const [textboxValue, setTextboxValue] = useState("");
    const [originalTextboxValue, setOriginalTextboxValue] = useState("");
    const [showCities, setShowCities] = useState(false);
    const [initialized, setInitialized] = useState(false);
    const [noResults, setNoResults] = useState(false);
    const [date, setDate] = useState({
        date: facilitySearchContext.searchDate,
        required: false,
        elementType: "date",
    });

    const listOfAllCities = facilitySearchContext.cityNames;
    const mainCities = facilitySearchContext.mainCities.some(
        (city) => city.length,
    )
        ? facilitySearchContext.mainCities
        : facilitySearchContext.country === "fi"
          ? ["Helsinki", "Tampere", "Turku"]
          : facilitySearchContext.country === "se"
            ? ["Stockholm", "Malmö", "Göteborg"]
            : ["Oslo", "Bergen", "Stavanger"];
    const countryName =
        facilitySearchContext.country === "fi"
            ? lang.finland
            : facilitySearchContext.country === "se"
              ? lang.sweden
              : lang.norway;
    const [currentSuggestions, setCurrentSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [selectedSuggestion, setSelectedSuggestion] = useState(null);
    const [autocompleteSessionToken, setAutocompleteSessionToken] = useState(
        new google.maps.places.AutocompleteSessionToken(),
    );
    const [pending, setPending] = useState(false);
    const [debouncedValue, setDebouncedValue] = useState("");
    const [typedValue, setTypedValue] = useState("");
    const [update, setUpdate] = useState(true);
    const STP = facilitySearchContext.filterSelection.STP;

    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const searchQuery = urlParams.get("address");
        const initialValue =
            (facilitySearchContext.selectedLocation &&
                facilitySearchContext.selectedLocation.address) ??
            searchQuery;

        setTextboxValue(initialValue);
        setOriginalTextboxValue(initialValue ?? "");

        window.addEventListener("popstate", initPopstateListener);

        () => {
            window.removeEventListener("popstate", initPopstateListener);
        };
    }, []);

    useEffect(() => {
        if (textboxRef.current.value !== originalTextboxValue) return;
        handleListItemClick();
    }, [date.date]);

    useEffect(() => {
        if (
            initialized ||
            !textboxRef ||
            facilitySearchContext.showLoadingScreen ||
            !facilitySearchContext.selectedLocation
        )
            return;
        if (
            !facilitySearchContext.selectedLocation?.address &&
            facilitySearchContext.selectedLocation?.name !== "georesult"
        )
            setTimeout(() => textboxRef.current.focus(), 500);
        setInitialized(true);
    }, [
        facilitySearchContext.showLoadingScreen,
        facilitySearchContext.selectedLocation,
    ]);

    useEffect(() => {
        const spanLabels = document.querySelectorAll(".input-label");
        const textInputField = document.querySelector(
            ".facilitysearchpage__textbox",
        );
        textInputField.after(spanLabels[0]);
        //no start date (CPS-24701)
        /*const dateInputField = document.querySelector(
            ".facilitysearchpage__control--date",
        );
        dateInputField.querySelector("input").after(spanLabels[1]);*/
    }, []);

    useEffect(() => {
        if (!typedValue.length) {
            setDebouncedValue(textboxRef.current?.value);
            return;
        }
        const delay =
            typedValue.length < 3 ? (typedValue.length - 1) * 250 : 750;
        const timeoutId = setTimeout(() => {
            setDebouncedValue(textboxRef.current?.value);
        }, delay);
        return () => clearTimeout(timeoutId);
    }, [typedValue]);

    const closeAll = (e) => {
        const suggestionList = document.querySelector(".pac-container");
        const citiesList = document.querySelector(
            ".facilitysearchpage__citieslist",
        );
        if (
            !e.relatedTarget ||
            (!e.relatedTarget.classList.contains(
                "facilitysearchpage__button",
            ) &&
                suggestionList &&
                !suggestionList.contains(e.relatedTarget))
        )
            setShowSuggestions(false);
        if (
            !e.relatedTarget ||
            (citiesList && !citiesList.contains(e.relatedTarget))
        )
            setShowCities(false);
        if (
            !e.relatedTarget ||
            (!e.relatedTarget
                .closest("DIV")
                .classList.contains("DayPickerInput") &&
                !e.relatedTarget
                    .closest("DIV")
                    .classList.contains("DayPicker-Day"))
        )
            textboxRef.current.value = originalTextboxValue;
        setNoResults(false);
    };

    const dateChanged = (date) =>
        (!date && facilitySearchContext.searchDate) ||
        (date &&
            moment(parseDate(date)).locale("sv").format("L") !==
                facilitySearchContext.searchDate);

    const updateFacilitiesOnMap = async (selectedLocation) => {
        const allFacilities = await search.getNearbyFacilities(
            {
                lang: facilitySearchContext.episerver.selectedlanguage,
                location: selectedLocation.position,
                init: true,
            },
            false,
        );

        if (allFacilities.status !== 200 && allFacilities.body.length > 0) {
            setFacilitySearchContext({
                ...facilitySearchContext,
                showLoadingScreen: true,
            });
            return;
        }

        const filteredFacilities = FilterFacilities(
            allFacilities.body,
            facilitySearchContext.filterSelection,
        );

        setSearchHistory(selectedLocation);
        setFacilitySearchContext({
            ...facilitySearchContext,
            panOnLoad: filteredFacilities.length ? true : false,
            selectedLocation,
            focusedFacility: null,
            mapZoom: 15,
            allFacilities: allFacilities.body,
            filteredFacilities,
            visibleFacilities: null,
            lastKnownLocation: selectedLocation.position,
            updateMarkers: true,
        });
    };

    const clearAddressInput = () => {
        textboxRef.current.value = "";
        setOriginalTextboxValue("");
        setCurrentSuggestions([]);
        setSelectedSuggestion(null);
        setTypedValue("");
        setNoResults(false);
    };

    const handleKeyDown = (e) => {
        if (
            (e.key === "Backspace" || e.key === "Delete") &&
            originalTextboxValue.length > 2 &&
            textboxRef.current?.value.length <= originalTextboxValue.length - 1
        ) {
            e.preventDefault();
            clearAddressInput();
        }
    };

    const handleKeyUp = async (e) => {
        handleListItems(e);

        if (
            e.key?.length > 1 &&
            textboxRef.current?.value === originalTextboxValue
        )
            return;
        if (!textboxRef.current.value) {
            setCurrentSuggestions([]);
            setSelectedSuggestion(null);
            setTypedValue("");
            setNoResults(false);
            return;
        }
        setTypedValue(textboxRef.current.value);
    };

    useEffect(() => {
        const fetchSuggestions = async () => {
            const request = {
                input: debouncedValue,
                language: facilitySearchContext.episerver.selectedlanguage,
                region: facilitySearchContext.country,
                includedRegionCodes: [facilitySearchContext.country],
                sessionToken: autocompleteSessionToken,
            };

            const { suggestions } =
                await google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions(
                    request,
                );

            if (suggestions.length > 0) {
                setNoResults(false);
                setCurrentSuggestions(suggestions.slice(0, 5));
                setSelectedSuggestion(suggestions[0]);
            } else {
                setNoResults(true);
                setCurrentSuggestions([]);
                setSelectedSuggestion(null);
            }
        };
        if (debouncedValue && update) fetchSuggestions();
        setUpdate(true);
    }, [debouncedValue]);

    const handleListItemClick = async (e) => {
        if (dateChanged(date.date)) {
            setPending(true);
            setFacilitySearchContext({
                ...facilitySearchContext,
                searchDate: date.date
                    ? moment(parseDate(date.date)).locale("sv").format("L")
                    : "",
            });
            if (date.date)
                window.sessionStorage.setItem(
                    "selected_date",
                    JSON.stringify(date),
                );
            else window.sessionStorage.removeItem("selected_date");
            setTimeout(() => {
                setPending(false);
            }, 500);
        } else if (!date.date)
            window.sessionStorage.removeItem("selected_date");
        if (!e)
            e = selectedSuggestion
                ? selectedSuggestion.placePrediction.placeId
                : currentSuggestions.length > 0
                  ? currentSuggestions[0].placePrediction.placeId
                  : null;
        const clickedID =
            e && e.currentTarget
                ? e.currentTarget.getAttribute("data-place-id")
                : e;
        if (!clickedID) return;
        setPending(true);
        const clickedSuggestion = currentSuggestions.find(
            (suggestion) => suggestion.placePrediction.placeId === clickedID,
        );
        setSelectedSuggestion(clickedSuggestion);
        const name = clickedSuggestion.placePrediction.text.toString();
        const selectedPlace = clickedSuggestion.placePrediction.toPlace();
        textboxRef.current.value = name;

        await selectedPlace.fetchFields({
            fields: ["formattedAddress", "location"],
        });

        const placePosition =
            selectedPlace && selectedPlace.location
                ? {
                      lat: +selectedPlace.location.lat().toFixed(5),
                      lng: +selectedPlace.location.lng().toFixed(5),
                  }
                : null;

        if (!placePosition) {
            textboxRef.current.focus();
            return;
        }
        setFacilitySearchContext({
            ...facilitySearchContext,
            showLoadingScreen: true,
        });
        const selectedLocation = {
            name: name.split(", ")[0],
            address: selectedPlace.formattedAddress,
            position: placePosition,
        };
        if (
            !facilitySearchContext.selectedLocation ||
            facilitySearchContext.selectedLocation.position.lat !==
                selectedLocation.position.lat ||
            facilitySearchContext.selectedLocation.position.lng !==
                selectedLocation.position.lng
        ) {
            updateFacilitiesOnMap(selectedLocation);
        } else {
            setFacilitySearchContext({
                ...facilitySearchContext,
                focusedFacility: null,
                mapZoom: 15,
            });
        }
        setOriginalTextboxValue(textboxRef.current.value);
        setShowSuggestions(false);
        setAutocompleteSessionToken(
            new google.maps.places.AutocompleteSessionToken(),
        );
        setCurrentSuggestions([]);
        setSelectedSuggestion(null);
        setPending(false);
    };

    const handleListItems = (e) => {
        if (
            currentSuggestions.length === 0 &&
            date.date &&
            dateChanged(date.date)
        )
            handleListItemClick();
        if (currentSuggestions.length === 0) return;
        let index = selectedSuggestion
            ? currentSuggestions.findIndex(
                  (suggestion) =>
                      suggestion.placePrediction.placeId ===
                      selectedSuggestion.placePrediction.placeId,
              )
            : -1;
        const focusedListItem =
            e.key === "Tab" && e.currentTarget.getAttribute("data-place-id");
        if (focusedListItem) {
            const focusedID = e.currentTarget.getAttribute("data-place-id");
            if (!focusedID) return;
            index = currentSuggestions.findIndex(
                (suggestion) =>
                    suggestion.placePrediction.placeId === focusedID,
            );
        }
        if (e.key === "Enter") {
            handleListItemClick({
                currentTarget: document.querySelector(".pac-item-selected"),
            });
        }
        if (e.key === "ArrowDown" || e.key === "ArrowUp" || focusedListItem) {
            setUpdate(false);
            if (e.key === "ArrowDown") {
                if (index === 4) index = 0;
                else index += 1;
            }
            if (e.key === "ArrowUp") {
                if (index === 0) return;
                else index -= 1;
            }
            if (!currentSuggestions[index]) return;
            setSelectedSuggestion(currentSuggestions[index]);
            const name =
                currentSuggestions[index].placePrediction.text.toString();
            textboxRef.current.value = name;
        }
    };

    const highlightText = (suggestionText) => {
        let matchingString = "";
        const searchValueArray = textboxRef.current.value.split("");
        const compareText = suggestionText
            ? suggestionText
                  .toLowerCase()
                  .replace(/å/g, "a")
                  .replace(/ä/g, "a")
                  .replace(/ö/g, "o")
            : "";

        searchValueArray.forEach((character, i) => {
            if (!compareText.toLowerCase().includes(matchingString)) return;
            matchingString += character
                .toLowerCase()
                .replace(/å/g, "a")
                .replace(/ä/g, "a")
                .replace(/ö/g, "o");
        });

        const beforeMatchingString = suggestionText.substring(
            0,
            compareText.indexOf(matchingString),
        );
        const afterMatchingString = suggestionText.substring(
            compareText.indexOf(matchingString) + matchingString.length,
            suggestionText.length,
        );
        const highlightedText = suggestionText.substring(
            compareText.indexOf(matchingString),
            compareText.indexOf(matchingString) + matchingString.length,
        );

        return (
            <>
                {beforeMatchingString}
                <strong>{highlightedText}</strong>
                {afterMatchingString}
            </>
        );
    };

    const handleButtonClick = async (e) => {
        if (
            originalTextboxValue === textboxRef.current.value &&
            !dateChanged(date.date)
        )
            setFacilitySearchContext({
                ...facilitySearchContext,
                returnToOriginalPosition: true,
                searchDate: date.date
                    ? moment(parseDate(date.date)).locale("sv").format("L")
                    : "",
            });
        else handleListItemClick();
    };

    const initPopstateListener = async (e) => {
        let state = e.state;
        if (state && state.name && state.address && state.position) {
            setFacilitySearchContext({
                ...facilitySearchContext,
                showLoadingScreen: true,
            });
            const allFacilities = await search.getNearbyFacilities(
                {
                    lang: facilitySearchContext.episerver.selectedlanguage,
                    location: state.position,
                    init: true,
                },
                false,
            );

            if (allFacilities.status !== 200 && allFacilities.body.length > 0) {
                setFacilitySearchContext({
                    ...facilitySearchContext,
                    showLoadingScreen: true,
                });
                return;
            }
            setFacilitySearchContext({
                ...facilitySearchContext,
                panOnLoad: allFacilities.body?.length ? true : false,
                selectedLocation: state,
                focusedFacility: null,
                mapZoom: 15,
                allFacilities: allFacilities.body,
                visibleFacilities: null,
                lastKnownLocation: state.position,
            });
            setTextboxValue(state.address);
        }
    };

    const handleCitiesClick = async (e) => {
        const city = `${e.target.textContent} ${countryName}`;
        const sessionToken = new google.maps.places.AutocompleteSessionToken();
        setFacilitySearchContext({
            ...facilitySearchContext,
            showLoadingScreen: true,
        });
        const request = {
            input: city,
            language: facilitySearchContext.episerver.selectedlanguage,
            region: facilitySearchContext.country,
            includedRegionCodes: [facilitySearchContext.country],
            sessionToken,
        };

        const { suggestions } =
            await google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions(
                request,
            );
        if (suggestions.length > 0) {
            setNoResults(false);
            const name = suggestions[0].placePrediction.text.toString();
            setTextboxValue(name);
            textboxRef.current.value = name;
            setOriginalTextboxValue(textboxRef.current.value);
            const place = suggestions[0].placePrediction.toPlace();

            await place.fetchFields({
                fields: ["formattedAddress", "location"],
            });

            const selectedLocation = {
                name: name.split(", ")[0],
                address: place.formattedAddress,
                position: place.location.toJSON(),
            };
            updateFacilitiesOnMap(selectedLocation);
        } else {
            setNoResults(true);
        }
        setCurrentSuggestions([]);
        setSelectedSuggestion(null);
        setShowCities(false);
    };

    const handleInputChange = (e) => {
        //const value = e.target.value;
        setFacilitySearchContext({
            ...facilitySearchContext,
            /*isSearchButtonEna: !value,*/
        });
    };

    const renderTextbox = () => {
        return (
            <div
                className="facilitysearchpage__textbox-wrapper"
                aria-busy={pending || facilitySearchContext.showLoadingScreen}
                onBlur={closeAll}
            >
                <input
                    onFocus={() => setShowSuggestions(true)}
                    onKeyDown={handleKeyDown}
                    onKeyUp={handleKeyUp}
                    className="facilitysearchpage__textbox"
                    type="text"
                    spellCheck="false"
                    autoComplete="off"
                    ref={textboxRef}
                    onChange={handleInputChange}
                    defaultValue={textboxValue}
                    placeholder={lang.location}
                    disabled={
                        facilitySearchContext.showLoadingScreen || pending
                    }
                />

                <span
                    className="input-label"
                    onClick={() => textboxRef.current.focus()}
                >
                    {lang.location}
                </span>

                <DateSelectionWithLabel
                    value={date}
                    setValue={(date) => setDate(date ?? "")}
                    locale={facilitySearchContext.country}
                    language={facilitySearchContext.episerver.selectedlanguage}
                    placeholder={lang.expectedstartingdate}
                    extraClass="facilitysearchpage__control--date"
                    disabled={STP}
                    showAllDates={false}
                    endDate={
                        facilitySearchContext.episerver.latestSalesorderStartDay
                    }
                >
                    <span className="input-label">
                        {lang.expectedstartingdate}
                    </span>
                </DateSelectionWithLabel>

                <button
                    className="facilitysearchpage__button"
                    aria-label={lang.searchbutton}
                    onClick={handleButtonClick}
                    disabled={
                        facilitySearchContext.showLoadingScreen || noResults
                    }
                >
                    {pending || facilitySearchContext.showLoadingScreen
                        ? ""
                        : lang.searchbutton}
                </button>

                <div className="aimo-spinner aimo-spinner--white aimo-spinner--mini">
                    <span></span>
                    <span></span>
                    <span></span>
                    <span></span>
                </div>

                {showSuggestions &&
                    (currentSuggestions.length > 0 || noResults) && (
                        <ul className="pac-container">
                            {currentSuggestions.length > 0
                                ? currentSuggestions.map((suggestion, i) => {
                                      const selected = selectedSuggestion
                                          ? selectedSuggestion.placePrediction
                                                .placeId ===
                                            suggestion.placePrediction.placeId
                                          : i === 0;
                                      return (
                                          <li
                                              key={i}
                                              onClick={handleListItemClick}
                                              onKeyUp={handleKeyUp}
                                              className={
                                                  selected
                                                      ? "pac-item pac-item-selected"
                                                      : "pac-item"
                                              }
                                              data-place-id={
                                                  suggestion.placePrediction
                                                      .placeId
                                              }
                                              tabIndex="0"
                                          >
                                              <span className="pac-icon pac-icon-marker"></span>
                                              {highlightText(
                                                  suggestion.placePrediction.text.toString(),
                                              )}
                                          </li>
                                      );
                                  })
                                : noResults && (
                                      <li className="pac-item">
                                          {
                                              facilitySearchContext.localization
                                                  .messages.noResultsMessage
                                          }
                                      </li>
                                  )}
                        </ul>
                    )}
            </div>
        );
    };

    return isLoaded ? renderTextbox() : null;
};

export default TextboxWidget;
